import { FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { UNSUBSCRIBE_EMAIL, RESUBSCRIBE_EMAIL } from 'graphql/service'
import { UnsubscribeScreenContainer } from './style'
import Checkbox from 'components/Checkbox'
import FormTextField from 'components/FormTextField'
import StyledButton from 'components/Button'
import { colors } from 'constants/colors';
import unsubscribeImage from 'assets/icons/unsubscribe.jpg'
import resubscribeImage from 'assets/icons/resub.jpg'
import { useGlobalAlerts } from 'customHooks/useGlobalAlerts';

const UnsubscribeSection: FC = () => {
  
  const { showGlobalMessage, showGlobalError } = useGlobalAlerts();
  const location = useLocation()
  
  const { search } = location
  const params = new URLSearchParams(search)
  const token = params.get('token')

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [reasons, setReasons] = useState<string[]>([])
  const [otherReason, setOtherReason] = useState('')

  const labels = [
    'Too many emails',
    'Content is not relevant',
    'Prefer not to receive any emails',
    'Found the content elsewhere',
    'Emails are too long',
    'Too much promotional content',
    'No longer interested in the topic',
    'Subscribed by mistake',
    'Emails are not visually appealing',
    'Privacy concerns',
    'Prefer to follow on social media',
    'Technical issues with email format',
  ]

  const [isResubscribed, setIsResubscribed] = useState(false)

  const checkboxes = labels.map((label, index) => {
    return (
      <Checkbox
        borderColor={colors.signInBorderColor}
        key={index}
        className="checkbox"
        onChange={() => {
          if (reasons.includes(label)) {
            const newReasons = reasons.filter((reason) => reason !== label)
            setReasons(newReasons)
          } else {
            const newReasons = [...reasons]
            newReasons.push(label)
            setReasons(newReasons)
          }
        }}
        spaceBetween={'16px'}
        label={label}
        checkboxScale={1.4}
        checked={reasons.includes(label)}
      />
    )
  })

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }, 500)
  }, [isSubmitted, isResubscribed])

  const [unsubscribe] = useMutation(UNSUBSCRIBE_EMAIL, {
    onCompleted: () => {
      setIsSubmitted(true)
      showGlobalMessage('You have been successfully unsubscribed.');
    },
    onError: () => {
      showGlobalError ('Unsubscribe failed!');
      console.log('Unsubscribe failed!')
    }
  })

  const [resubscribe] = useMutation(RESUBSCRIBE_EMAIL, {
    onCompleted: () => {
      setIsResubscribed(true)
      showGlobalMessage('You have been successfully resubscribed.');
    },
    onError: () => {
      showGlobalError ('Resubscribe failed!');
      console.log('Resubscribe failed!')
    }
  })

  const handleSubmit = () => {
    const newReasons = [...reasons]
    if (otherReason.length > 0) {
      newReasons.push(otherReason)
    }

    unsubscribe({
      variables: {
        ...(token && { id: token }),
        ...(newReasons.length > 0 && {
          reason: newReasons.join('; ')
        })
      }
    })
  }

  const handleResubscribe = () => {
    resubscribe({
      variables: {
        ...(token && { id: token }),
      }
    })
  }
  
  useEffect(() => {
    if (!token) {
      showGlobalError('Invalid unsubscribe link');
    }
  }, [token, showGlobalError]);


  return (
    <UnsubscribeScreenContainer>
      <div
        className={`titleContainer ${isResubscribed ? 'titleContainerResubscribed' : ''
          }`}
      >
        <div className="innerContainerTop">
          <div className='titleText'>
            {!isSubmitted  ? (
              <div className="title">
                <div>Don’t leave us all alone!</div>
                <div className='highLight'>Not like this!</div>
              </div>
            ) : null }
            
            {isSubmitted && !isResubscribed ?
            (
              <div className="title">
                <div>Your abandonment is</div>
                <div>complete.</div>
              </div>
            ): null}

          {isResubscribed ?
            (
              <div className="title">
                <div>Welcome back!</div>
                <div>We missed <br className='missed' /> you so much!</div>
              </div>
            ): null}
          </div>
          <div className="unsubscribeImage">
            {isResubscribed ?
              (
                <img
                  src={resubscribeImage}
                  className="resubscribeImageImg"
                  alt="Unsubscribe"
                />
              ) : (
                <img
                  src={unsubscribeImage}
                  className="unsubscribeImageImg"
                  alt="Resubscribe"
                />
              )
            }
          </div>
        </div>
      </div>

      <div className="inputRow">
        {!isSubmitted ? (
          <div className="innerContainer">
            <div className='titleText'>
              Tell Us Why You’re Breaking Up with Us
            </div>

            <div className='checkboxes'>{checkboxes}</div>

            <div className='otherField'>
              <FormTextField
                className="textField"
                label={'Other:'}
                maxRows={5}
                minRows={5}
                labelSize='16px'
                multiline
                borderColor={colors.signInBorderColor}
                value={otherReason}
                onChange={(e) => {
                  setOtherReason(e.target.value)
                }}
              />
            </div>

            <div className='buttonContainer'>
              <StyledButton
                width="134px"
                height="48px"
                margin='16px 59px 16px 59px'
                onClick={handleSubmit}
              >
                Unsubscribe
              </StyledButton>
            </div>
          </div>
        ) : null }
          { !isResubscribed && isSubmitted ?
          (
          <div className="innerContainer">
            <div className='description'>
              Thank you for providing your feedback.
            </div>
            <div className='description'>
              You will no longer receive any emails from us.
            </div>
            <div className='description'>
              If you have any questions or need further assistance,
              please contact our support team at{' '}
              <a href='mailto: help@rookhotel.com'>help@rookhotel.com</a>.
            </div>

            <div className='buttonContainer'>
              <StyledButton
                width="149px"
                height="48px"
                margin='16px 59px 16px 59px'
                onClick={handleResubscribe}
              >
                Resubscribe
              </StyledButton>
            </div>
          </div>
        ) : null}

        {isResubscribed && (
          <div className="innerContainer">
            <div className='description'>
              We’re so excited to have you with us again.
            </div>
            <div className='description'>
              Your subscription is now active.
            </div>
            <div className='description'>
              If you have any questions or need further assistance,
              please contact our support team at{' '}
              <a href='mailto:help@rookhotel.com'>help@rookhotel.com</a>.
            </div>
          </div>
        )}
      </div>
    </UnsubscribeScreenContainer>
  )
}

export default UnsubscribeSection
